import Swiper from 'swiper/bundle'

// Lowercase and strip accents so "lowchen" matches "Löwchen"
const normalize = (text) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

window.customElements.define('shop-by-breed', class ShopByBreed extends HTMLElement {
  connectedCallback() {
    const carousel = this.querySelector('.shop-by-breed__carousel')
    if (!carousel) return

    this.swiper = new Swiper(carousel, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      pagination: {
        el: this.querySelector('.shop-by-breed__pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: this.querySelector('.shop-by-breed__next'),
        prevEl: this.querySelector('.shop-by-breed__previous'),
      },
    })

    // Remember each slide's default image so it can be restored later
    this.querySelectorAll('.shop-by-breed__image').forEach((img) => {
      img.dataset.defaultSrc = img.getAttribute('src') || ''
      img.dataset.defaultSrcset = img.getAttribute('srcset') || ''
      img.dataset.defaultAlt = img.getAttribute('alt') || ''
    })

    // Remember each category link's default URL so it can be restored later
    this.querySelectorAll('a.shop-by-breed__category').forEach((link) => {
      link.dataset.defaultHref = link.getAttribute('href') || ''
    })

    // Remember the View All link's default URL so it can be restored later
    this.viewAll = this.querySelector('.shop-by-breed__view-all')
    if (this.viewAll) {
      this.viewAll.dataset.defaultHref = this.viewAll.getAttribute('href') || ''
    }


    this.initSearch()
  }

  /* ---------- Breed search (type-ahead combobox) ---------- */

  initSearch() {
    this.input = this.querySelector('.shop-by-breed__input')
    if (!this.input) return

    const combobox = this.querySelector('.shop-by-breed__combobox')
    this.dropdown = this.querySelector('.shop-by-breed__dropdown')
    this.listbox = this.querySelector('.shop-by-breed__listbox')
    this.noResults = this.querySelector('.shop-by-breed__no-results')
    this.clearButton = this.querySelector('.shop-by-breed__clear')
    this.toggleButton = this.querySelector('.shop-by-breed__toggle')
    this.options = Array.from(this.querySelectorAll('.shop-by-breed__option'))
    this.visible = this.options
    this.activeIndex = -1
    this.selected = null

    this.options.forEach((option) => {
      option.dataset.originalText = option.textContent.trim()
      option.dataset.search = normalize(option.dataset.originalText)
    })

    this.input.addEventListener('focus', () => this.open())
    this.input.addEventListener('click', () => this.open())
    this.input.addEventListener('input', () => this.onInput())
    this.input.addEventListener('keydown', (event) => this.onKeydown(event))

    // Keep focus in the input while interacting with the buttons and list
    this.toggleButton.addEventListener('mousedown', (event) => event.preventDefault())
    this.toggleButton.addEventListener('click', () => {
      if (this.isOpen()) {
        this.close()
      } else {
        this.input.focus()
        this.open()
      }
    })

    this.clearButton.addEventListener('click', () => {
      this.reset()
      this.input.focus()
      this.open()
    })

    this.dropdown.addEventListener('mousedown', (event) => event.preventDefault())
    this.dropdown.addEventListener('click', (event) => {
      const option = event.target.closest('.shop-by-breed__option')
      if (option) this.select(option)
    })
    this.dropdown.addEventListener('mousemove', (event) => {
      const option = event.target.closest('.shop-by-breed__option')
      if (!option) return
      const index = this.visible.indexOf(option)
      if (index !== this.activeIndex) this.setActive(index, false)
    })

    combobox.addEventListener('focusout', (event) => {
      if (!combobox.contains(event.relatedTarget)) this.commit()
    })
  }

  isOpen() {
    return !this.dropdown.hidden
  }

  open() {
    if (this.isOpen()) return
    this.dropdown.hidden = false
    this.input.setAttribute('aria-expanded', 'true')
    this.setActive(this.selected ? this.visible.indexOf(this.selected) : -1)
  }

  close() {
    this.dropdown.hidden = true
    this.input.setAttribute('aria-expanded', 'false')
    this.clearActive()
  }

  clearActive() {
    const current = this.querySelector('.shop-by-breed__option.is-active')
    if (current) current.classList.remove('is-active')
    this.activeIndex = -1
    this.input.removeAttribute('aria-activedescendant')
  }

  setActive(index, scroll = true) {
    this.clearActive()
    const option = this.visible[index]
    if (!option) return

    this.activeIndex = index
    option.classList.add('is-active')
    this.input.setAttribute('aria-activedescendant', option.id)

    if (scroll) {
      const top = option.offsetTop
      const bottom = top + option.offsetHeight
      if (top < this.dropdown.scrollTop) {
        this.dropdown.scrollTop = top
      } else if (bottom > this.dropdown.scrollTop + this.dropdown.clientHeight) {
        this.dropdown.scrollTop = bottom - this.dropdown.clientHeight
      }
    }
  }

  filter(query) {
    this.clearActive()

    const q = normalize(query)

    this.visible = q
      ? this.options.filter((option) =>
          option.dataset.search.includes(q)
        )
      : this.options

    this.options.forEach((option) => {
      const originalText =
        option.dataset.originalText || option.textContent.trim()

      option.dataset.originalText = originalText

      // Determine whether this breed matches the search
      const normalizedText = normalize(originalText)
      const index = q ? normalizedText.indexOf(q) : -1
      const matches = !q || index !== -1

      // Explicitly hide nonmatching options
      option.hidden = !matches
      option.style.display = matches ? '' : 'none'

      // Restore the original breed name
      option.textContent = originalText

      // Highlight the matching text
      if (q && matches) {
        const length = query.trim().length
        const mark = document.createElement('mark')

        mark.textContent = originalText.slice(index, index + length)

        option.replaceChildren(
          document.createTextNode(originalText.slice(0, index)),
          mark,
          document.createTextNode(originalText.slice(index + length))
        )
      }
    })

    // Show or hide the list and no-results message
    this.listbox.hidden = this.visible.length === 0
    this.noResults.hidden = this.visible.length > 0
  }

  onInput() {
    const value = this.input.value

    // Emptying the field is the same as clearing the breed
    if (!value && this.selected) this.reset()

    this.open()
    this.filter(value)
    this.highlightMatches(value)
    this.setActive(value.trim() ? 0 : -1)
    this.updateClear()
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const wasOpen = this.isOpen()
        this.open()
        if (!this.visible.length) return
        // Opening with the arrow key just lands on the current breed (if any)
        if (!wasOpen && this.activeIndex !== -1) return

        const step = event.key === 'ArrowDown' ? 1 : -1
        const next = this.activeIndex === -1
          ? (step === 1 ? 0 : this.visible.length - 1)
          : (this.activeIndex + step + this.visible.length) % this.visible.length
        this.setActive(next)
        break
      }
      case 'Enter': {
        if (!this.isOpen()) return
        const option = this.visible[this.activeIndex]
          || (this.visible.length === 1 ? this.visible[0] : null)
        if (option) {
          event.preventDefault()
          this.select(option)
        }
        break
      }
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault()
          this.commit()
        }
        break
    }
  }

  select(option) {
    this.selected = option
    this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)))
    this.applyBreed(option.dataset.value, option.dataset.originalText)
    this.commit()
  }

  // Close the list and make the input show the chosen breed (or nothing)
  commit() {
    this.close()
    this.highlightMatches('')
    this.input.value = this.selected
      ? this.selected.dataset.originalText
      : ''
    this.filter('')
    this.updateClear()
  }

  reset() {
    this.selected = null
    this.options.forEach((item) => item.setAttribute('aria-selected', 'false'))
    this.input.value = ''
    this.filter('')
    this.applyBreed('')
    this.updateClear()
  }

  updateClear() {
    this.clearButton.hidden = !this.input.value && !this.selected
  }

  /* ---------- Link, All Link and Image swapping ---------- */

  applyBreed(breed, breedName = '') {
    this.querySelectorAll('.shop-by-breed__slide').forEach((slide) => {
      // --- View All link ---
      if (this.viewAll) {
        if (breed) {
          const base = this.viewAll.dataset.collectionsUrl || '/collections'
          this.viewAll.href = `${base}/${breed}`
        } else {
          this.viewAll.href = this.viewAll.dataset.defaultHref
        }
      }


      // --- Link swapping ---
      const link = slide.querySelector('a.shop-by-breed__category')
      if (link && link.dataset.defaultHref) {
        const base = link.dataset.defaultHref
        if (breedName) {
          const narrow = encodeURIComponent(JSON.stringify([['Breed', breedName]]))
          const separator = base.includes('?') ? '&' : '?'
          link.href = `${base}${separator}narrow=${narrow}`
        } else {
          link.href = base
        }
      }

      // --- Image swapping ---
      const wrap = slide.querySelector('.shop-by-breed__image-wrap')
      const variants = slide.querySelector('.shop-by-breed__variants')
      let img = slide.querySelector('.shop-by-breed__image')

      // Look for a breed-specific image for this collection
      const match = breed && variants
        ? Array.from(variants.querySelectorAll('[data-breed]')).find((el) => el.dataset.breed === breed)
        : null

      if (match) {
        // A category block with no default image has no <img> yet, so create one
        if (!img && wrap) {
          img = document.createElement('img')
          img.className = 'shop-by-breed__image'
          img.loading = 'lazy'
          img.sizes = '148px'
          img.dataset.defaultSrc = ''
          img.dataset.defaultSrcset = ''
          img.dataset.defaultAlt = ''
          wrap.appendChild(img)
        }
        if (!img) return

        img.src = match.dataset.src
        img.srcset = match.dataset.srcset
        img.alt = match.dataset.alt
      } else if (img) {
        // No breed selected, or no image set for this breed: show the default
        if (img.dataset.defaultSrc) {
          img.src = img.dataset.defaultSrc
          img.srcset = img.dataset.defaultSrcset
          img.alt = img.dataset.defaultAlt
        } else {
          // Image was created by a breed swap and there is no default to restore
          img.remove()
        }
      }
    })
  }

  highlightMatches(query) {
    const q = normalize(query)

    this.options.forEach((option) => {
      // Restore the original breed name before highlighting
      option.textContent = option.dataset.originalText ||
        option.textContent

      if (!option.dataset.originalText) {
        option.dataset.originalText = option.textContent
      }

      if (!q) return

      const text = option.dataset.originalText
      const normalizedText = normalize(text)
      const index = normalizedText.indexOf(q)

      if (index === -1) return

      // Match the original text while accounting for accents
      const start = index
      const end = start + query.trim().length

      const before = document.createTextNode(text.slice(0, start))
      const mark = document.createElement('mark')
      mark.textContent = text.slice(start, end)
      const after = document.createTextNode(text.slice(end))

      option.replaceChildren(before, mark, after)
    })
  }

  disconnectedCallback() {
    this.swiper?.destroy()
  }
})
